import { type ComputedRef, type Ref, computed, ref, watch } from "vue";



import { Factory } from "@/data/dao/Factory";
import type { DaoError } from "@/data/dao/service/BaseServiceDao";
import { CustomerDao } from "@/data/dao/service/CustomerDao";
import { OperatorDao } from "@/data/dao/service/OperatorDao";
import { dataEvents } from "@/data/events/dataEvents";
import type { IPaymentIntent, IPaymentMethod } from "@/data/model/Customer";
import { PaymentProvider, UiPaymentState, UserItemSelectionChangeReason } from "@/data/model/Enums";
import type { BillItemSelectionChangedEvent } from "@/data/model/Events";
import { onApplyDiscountErrorOccurred } from "@/data/services/DiscountsService";
import { getIsApplyVouchersAllowedForOrderUseCase, getIsApplyVouchersAllowedForPayUseCase } from "@/data/services/PaymentService";
import { Format } from "@/data/util/Format";
import { Misc } from "@/data/util/Misc";



import { translations } from "@/shared/localization/translations";
import { logError } from "@/shared/logging/errors";
import { Ripple } from "@/shared/ui/Ripple";
import { toMoneyWithSign } from "@/shared/utils/format";
import { type VueComponentInstance, createVueComponent } from "@/shared/utils/vue";



import ContactDataSection from "@/components/pay/ContactDataSection.vue";
import DeliveryAddressSection from "@/components/pay/DeliveryAddress/DeliveryAddressSection.vue";
import ApplyDiscountSection from "@/components/vouchers/ApplyDiscountSection.vue";



import { config } from "@/app/App";
import { TastierClient } from "@/app/TastierClient";
import AppTipSummaryTable from "@/app/components/payment/AppTipSummaryTable.vue";
import PaymentMethodSelectorComponent from "@/app/components/payment/PaymentMethodSelectorComponent.vue";
import { PaymentMethodSelectorMode } from "@/app/components/payment/PaymentMethodSelectorMode";
import { BaseController } from "@/app/controller/BaseController";
import { NumberInput } from "@/app/elements/NumberInput";
import { PayButton } from "@/app/elements/PayButton";
import { QuickPaymentMethodSelector } from "@/app/elements/QuickPaymentMethodSelector";
import { Slider } from "@/app/elements/Slider";
import { appEvents } from "@/app/events/appEvents";
import { globalStores } from "@/app/globalStores";
import { trans } from "@/app/localization/Trans";
import { UserCreditCardsService } from "@/app/services/userCreditCardsService";
import { Helper } from "@/app/ui/Helper";
import { Popup } from "@/app/ui/Popup";
import { UserMenu } from "@/app/ui/UserMenu";


type State = {
    consumption: Ref<number>;
    discount: Ref<number>;
    tip: Ref<number>;
    fees: Ref<number>;
    totalAfterDiscount: Ref<number>;
    isPayService: Ref<boolean>;
    showTip: Ref<boolean>;
    isTotalFullyCoveredByDiscounts: ComputedRef<boolean>;
    tipAndPayFormFocused: Ref<boolean>;
    isQuickPaymentMethodSelectorVisible: ComputedRef<boolean>;
    hasVouchers: ComputedRef<boolean>;
    hasDiscounts: ComputedRef<boolean>;
};

//TODO: CHECK VISIBILITY OF FIELDS WHEN TIP IS FOCUSED
export class PayController extends BaseController {
    public onGoBack?: { () };

    private readonly customerDao: CustomerDao;
    private readonly $scrollable: JQuery;
    private readonly $tip: JQuery;
    private readonly $title: JQuery;

    private tipSlider?: Slider;
    private readonly $tipInputRow?: JQuery;
    private tipInput?: NumberInput;
    private readonly $receivedRow?: JQuery;
    private receivedInput?: NumberInput;
    private useTip: boolean = true;

    private $paymentMethodSelector: JQuery;
    private paymentMethodSelectorComponent?: VueComponentInstance<typeof PaymentMethodSelectorComponent>;
    private quickPaymentMethodSelector: QuickPaymentMethodSelector;
    private $signinSection: JQuery;

    private $applyDiscountSectionContainer!: JQuery;
    private applyDiscountSectionComponent?: VueComponentInstance<typeof ApplyDiscountSection>;
    private contactDataSection: VueComponentInstance<typeof ContactDataSection>;

    private $contactDataSection: JQuery;

    private $deliveryAddressSection: JQuery;

    private readonly $bottomContainer: JQuery;

    private payBtn: PayButton;
    public userMenu: UserMenu;
    public shouldShowSelectionsChangedPopupOnLeave: boolean = false;

    public $inputContainer: JQuery;
    private $newTotal: JQuery;
    //private tipSummaryTableComponent?: VueComponentInstance<typeof AppTipSummaryTable>;

    private currentState: State = {
        consumption: ref(0),
        discount: ref(0),
        tip: ref(0),
        fees: ref(0),
        totalAfterDiscount: ref(0),
        isPayService: ref(false),
        showTip: ref(true),
        isTotalFullyCoveredByDiscounts: computed(
            () =>
                this.currentState.discount.value > 0 &&
                this.currentState.consumption.value == this.currentState.discount.value &&
                this.currentState.tip.value == 0
        ),
        tipAndPayFormFocused: ref(false),
        isQuickPaymentMethodSelectorVisible: computed(
            () =>
                !this.currentState.tipAndPayFormFocused.value && !this.currentState.isTotalFullyCoveredByDiscounts.value
        ),
        hasVouchers: computed(() =>
            this.currentState.isPayService.value
                ? globalStores.billStore!.hasVouchers
                : globalStores.cartStore!.hasVouchers
        ),
        hasDiscounts: computed(() =>
            this.currentState.isPayService.value
                ? globalStores.billStore!.hasDiscounts
                : globalStores.cartStore!.hasDiscounts
        ),
    };

    constructor() {
        super("pay");
        const hdr = $('<div class="head"></div>').appendTo(this.$view);
        this.$title = $('<div class="title"></div>').appendTo(hdr);

        this.customerDao = Factory.getInstance<CustomerDao>(CustomerDao);

        this.updateTitle(TastierClient.payContext.isPayLater());

        Ripple.bind($('<a class="action left" href="#"><i class="fal fa-arrow-left"></i></a>'))
            .prependTo(hdr)
            .click((e) => {
                e.preventDefault();

                const statesToSkip = [UiPaymentState.Paying, UiPaymentState.Confirmed, UiPaymentState.Failed];
                if (statesToSkip.includes(TastierClient.payContext.state)) {
                    return;
                }

                if (this.onGoBack) {
                    this.onGoBack();
                }
            });

        this.userMenu = new UserMenu().appendTo(hdr);
        this.$scrollable = $('<div class="scrollable" />').appendTo(this.$view);
        this.$tip = $('<div class="tip" />').appendTo(this.$scrollable);

        // todo: what if the user changes location while using app? what if user logs out while using app? this tip slider is only created once, it should be verified on each visible change
        if (TastierClient.identityContext?.isWaiterInCurrentLocation()) {
            this.$tipInputRow = $(`<div class="row"><div class="label">${translations.pay.tip}</div></div>`).appendTo(
                this.$tip
            );

            this.tipInput = new NumberInput(undefined, {
                min: 0,
                decimals: 2,
                step: 0.01,
            }).renderTo(this.$tipInputRow);

            this.tipInput.onChange = (value) => {
                if (value! < 0) {
                    this.tipInput!.setValue(0);
                } else {
                    this.receivedInput!.setValue(value! + this.getConsumption());
                    TastierClient.payContext.setTip(value!);
                }

                this.resetSelectedPaymentMethodIfNeeded();
            };

            this.tipInput.onKeyPress = (value) => {
                if (value! >= 0) {
                    this.receivedInput!.setValue(value! + this.getConsumption());
                    TastierClient.payContext.setTip(value!);
                    this.resetSelectedPaymentMethodIfNeeded();
                }
            };

            this.tipInput.onFocus = (hasFocus) => {
                this.currentState.tipAndPayFormFocused.value = hasFocus;
                this.setApplyDiscountSectionVisibility(hasFocus);
            };

            this.$receivedRow = $(
                `<div class="row"><div class="label">${translations.pay.received}</div></div>`
            ).appendTo(this.$tip);
            this.receivedInput = new NumberInput(undefined, {
                min: 0,
                max: 999999,
                decimals: 2,
                step: 0.01,
            }).renderTo(this.$receivedRow);

            this.receivedInput.onFocus = (hasFocus) => {
                this.currentState.tipAndPayFormFocused.value = hasFocus;
                this.setApplyDiscountSectionVisibility(hasFocus);
            };

            this.receivedInput.onChange = (value) => {
                if (value! >= this.getConsumption()) {
                    this.tipInput!.setValue(value! - this.getConsumption());
                    TastierClient.payContext.setTip(this.tipInput!.getValue()!);
                    this.resetSelectedPaymentMethodIfNeeded();
                } else {
                    this.receivedInput!.setValue(this.getConsumption());
                }
            };
            this.receivedInput.onKeyPress = (value) => {
                if (value! >= this.getConsumption()) {
                    this.tipInput!.setValue(value! - this.getConsumption());
                    TastierClient.payContext.setTip(this.tipInput!.getValue()!);
                    this.resetSelectedPaymentMethodIfNeeded();
                }
            };
        } else {
            const tipRow = $(`<div class="row"><div class="label">${translations.pay.tip}</div></div>`).appendTo(
                this.$tip
            );

            this.tipSlider = new Slider(config.maxTip).setValue(TastierClient.payContext.tipPercent!).appendTo(tipRow);
            this.tipSlider.onChange = (v) => {
                TastierClient.payContext.setTipInPercent(v);
                this.resetSelectedPaymentMethodIfNeeded();
            };
        }

        this.$inputContainer = $('<div class="inputs-container"></div>').appendTo(this.$view);

        this.initializeApplyDiscountSection();

        this.$contactDataSection = $('<div style="margin-bottom: 6px;" class="contact-data form"></div>').appendTo(
            this.$inputContainer
        );

        this.contactDataSection = createVueComponent(
            ContactDataSection,
            {
                isInOperatorApp: false,
                onClickOpenProfileView: () => {
                    const ordererRequirements = Helper.getCurrentOrderServiceOrderApplication()?.ordererRequirements;
                    this.userMenu.showProfile(ordererRequirements?.isPhoneNumberRequired);
                },
                onStartEditing: () => {
                    console.log("ON START EDITING");
                    this.setApplyDiscountSectionVisibility(true);

                    this.showHideTip(true);
                },
                onEndEditing: () => {
                    console.log("ON END EDITING");
                    this.setApplyDiscountSectionVisibility(false);
                    this.showHideTip(false);
                },
            },
            this.$contactDataSection[0]
        ).mountedComponent;

        this.$deliveryAddressSection = $(
            '<div style="margin-bottom: 6px;" class="delivery-address form"></div>'
        ).appendTo(this.$inputContainer);

        createVueComponent(DeliveryAddressSection, {}, this.$deliveryAddressSection[0]).mountedComponent;

        this.$signinSection = $('<div class="signin" />').appendTo(this.$inputContainer);
        this.$signinSection.hide();

        $(`<button type="button" class="btn">${trans.pay.signup}</button>`)
            .appendTo(this.$signinSection)
            .click(async () => {
                const profile = TastierClient.identityContext.getProfile();
                const contactData = this.contactDataSection.getCurrentContactData();

                if (profile != null) {
                    const email = profile.email == null || profile.email.length === 0 ? contactData.email : undefined;
                    const firstName =
                        profile.firstName == null || profile.firstName.length === 0 ? contactData.firstName : undefined;
                    const lastName =
                        profile.lastName == null || profile.lastName.length === 0 ? contactData.lastName : undefined;
                    const phoneNumber =
                        profile.phoneNumber == null || profile.phoneNumber.length === 0
                            ? contactData.phoneNumber
                            : undefined;
                    const phoneNumberCountryCode =
                        profile.phoneNumber == null || profile.phoneNumber.length === 0
                            ? contactData.phoneNumberCountryCode
                            : undefined;
                    this.userMenu.showSignup(email, firstName, lastName, phoneNumber, phoneNumberCountryCode);
                } else {
                    this.userMenu.showSignup(contactData.email, contactData.firstName, contactData.lastName);
                }
            });

        this.$signinSection.append(`<div class="hint">${trans.pay.signupBenefits}</div>`);

        this.quickPaymentMethodSelector = new QuickPaymentMethodSelector().appendTo(this.$inputContainer);
        this.quickPaymentMethodSelector.onSelect = async (selectedMethod) => {
            if (selectedMethod != null) {
                await this.onPaymentMethodSelected(selectedMethod);
            }
        };

        this.$newTotal = $("<div></div>").appendTo(this.$inputContainer);
        const props = {
            totalConsumption: this.currentState.consumption,
            discountAmount: this.currentState.discount,
            tip: this.currentState.tip,
            fees: this.currentState.fees,
            showTip: this.currentState.showTip,
            isDiscountClickable: ref(true),
            hasDiscounts: this.currentState.hasDiscounts,
            isPayService: this.currentState.isPayService,
            onDiscountRemoved: () => {
                this.updateCurrentState();
            },
        };

        // Tip Summary Table Component
        createVueComponent(AppTipSummaryTable, props, this.$newTotal[0]).mountedComponent;

        this.$bottomContainer = $('<div class="bottom" />').appendTo(this.$view);
        this.$bottomContainer.append(`<div class="disclaimer">${trans.pay.disclaimer}</div>`);

        const btnContainer = $('<div class="btncontainer" />').appendTo(this.$bottomContainer);
        this.$paymentMethodSelector = $("<div></div>").appendTo(this.$bottomContainer);
        const propsPaymentMethodSelector = {
            totalConsumption: this.currentState.consumption,
            discountAmount: this.currentState.discount,
            tip: this.currentState.tip,
            hasVouchers: this.currentState.hasVouchers,
            isPayService: this.currentState.isPayService,
            setApplyDiscountSectionVisibility: () => this.setApplyDiscountSectionVisibility(),
            onModeChange: () => this.updateState(),
            onOpenClose: () => this.updateState(),
        };
        this.paymentMethodSelectorComponent = createVueComponent(
            PaymentMethodSelectorComponent,
            propsPaymentMethodSelector,
            this.$paymentMethodSelector[0]
        ).mountedComponent;

        this.payBtn = new PayButton(translations.pay.payNow).appendTo(btnContainer);
        this.payBtn.onClick = async () => {
            await this.onEnabledPayButtonClick();
        };
        this.payBtn.onDisabledClick = () => {
            this.onDisabledPayButtonClick();
        };

        watch(this.currentState.isQuickPaymentMethodSelectorVisible, (isVisible) => {
            this.quickPaymentMethodSelector.setVisibility(isVisible);
        });
        this.quickPaymentMethodSelector.setVisibility(this.currentState.isQuickPaymentMethodSelectorVisible.value);
    }

    private resetSelectedPaymentMethodIfNeeded() {
        const selectPaymentMethod = TastierClient.payContext.selectedPaymentMethod;
        if (selectPaymentMethod != null && selectPaymentMethod.provider === PaymentProvider.Stripe) {
            this.paymentMethodSelectorComponent?.reset();
        }
    }

    private initializeApplyDiscountSection() {
        if (TastierClient.context.currentAllocation == null) throw new Error("User is not allocated");

        const props = {
            enableQrScanner: true,
            inOperatorApp: false,
            locationId: TastierClient.context.currentAllocation!.locationId,
            orderServiceId: TastierClient.context.selectedServiceId,
            onFocus: (hasFocus: boolean) => {
                if (hasFocus) {
                    this.$tipInputRow?.hide();
                    this.$receivedRow?.hide();
                } else {
                    this.$tipInputRow?.show();
                    this.$receivedRow?.show();
                }
            },
            onApplyDiscountErrorOccurred: (error: DaoError) => onApplyDiscountErrorOccurred(error),
        };

        this.$applyDiscountSectionContainer = $("<div></div>").appendTo(this.$inputContainer);
        this.applyDiscountSectionComponent = createVueComponent(
            ApplyDiscountSection,
            props,
            this.$applyDiscountSectionContainer[0]
        ).mountedComponent;
    }

    private async onEnabledPayButtonClick() {
        if (
            this.paymentMethodSelectorComponent?.paymentMethodSelectorMode === PaymentMethodSelectorMode.StripeCardForm
        ) {
            try {
                await this.paymentMethodSelectorComponent?.validateCardForm();
                await this.validateAndPay();
            } catch (ex) {
                logError(ex);
            }
        } else {
            await this.validateAndPay();
        }
    }

    private onDisabledPayButtonClick() {
        if (TastierClient.payContext.state === UiPaymentState.MethodSelection) {
            this.paymentMethodSelectorComponent?.open();
        }
    }

    protected async onBecomeVisible(data?: { isMainNavigation: boolean; isForPayService: boolean }) {
        const isPayService = Helper.isPayService();
        const isInit = data?.isMainNavigation == true;

        // the T&C row is for end users and its links would open a web page on a terminal, so the
        // Mobile POS does not show it. checked on every visit, the guest of the app may become an operator
        this.$view.toggleClass("no-disclaimer", globalStores.identityStore?.hasOperatorAccess === true);

        // register event handlers
        TastierClient.payContext.onAmountChanged.add(this.updateAmount, this);
        TastierClient.payContext.onStateChanged.add(this.updateState, this);
        TastierClient.payContext.onIntentChanged.add(this.intentUpdated, this);
        TastierClient.identityContext.onIdentityChange.add(this.onAuthenticationChanged, this);
        UserCreditCardsService.onCreditCardsChanged.add(this.onCreditCardsChanged, this);

        // notify about a cart when user uses order service
        // notify about item selections changed when user uses pay service
        if (isPayService) {
            dataEvents.appSocket.ItemSelectionChanged.add(this.onItemSelectionChanged, this);
            TastierClient.payContext.onBillDiscountChanged.add(this.updateCurrentState, this);
        } else {
            TastierClient.context.onCartChanged.add(this.onCartUpdated, this);
            TastierClient.context.onCartDiscountChanged.add(this.updateCurrentState, this);
        }

        // Set up the component: Apply discount
        this.applyDiscountSectionComponent?.reset();

        this.contactDataSection?.reset();

        if (isPayService) {
            this.applyDiscountSectionComponent?.setBillId(TastierClient.context.currentBill!.id);
        } else {
            this.applyDiscountSectionComponent?.setCartId(TastierClient.context.getCurrentCartId());
        }

        // if user didn't come from main view - no need to init state, only recover it
        if (isInit) {
            // set initial state - the state that user sees and amounts that user agreed to pay
            // this shouldn't change after user enters this page
            TastierClient.payContext.renewConsumption();
            this.currentState.consumption.value = TastierClient.payContext.consumption;
            this.currentState.discount.value = TastierClient.payContext.discountAmount;
            this.currentState.fees.value = TastierClient.payContext.fees;
            this.currentState.totalAfterDiscount.value = TastierClient.payContext.totalExclTipAfterDiscounts;
            this.currentState.isPayService.value = Helper.isPayService();

            this.shouldShowSelectionsChangedPopupOnLeave = false;
        }

        // todo: do not remove! without it the consumption is not being updated
        this.enableDisableTip();

        // if user came from the services main view - init state
        this.paymentMethodSelectorComponent?.setLoading();
        try {
            await TastierClient.payContext.prepareForCheckout();

            // preparing the payment stops the running timers of the selection, which fixes their prices: the bill is
            // loaded again, so the amounts shown are the ones the payment was prepared for
            if (TastierClient.payContext.intent?.areTimersStopped === true) {
                await globalStores.billStore!.loadCurrentBill();
                this.updateCurrentState();
            }
        } catch (ex) {
            logError(ex);
        }

        this.checkLoginButtonVisibility();
        this.updateAmount();
        this.updateState();

        this.setApplyDiscountSectionVisibility();

        appEvents.payContext.totalDiscountMismatchPopupViewed.add(this.onTotalDiscountMismatchPopupViewed, this);
    }

    protected onBecomeInvisible() {
        TastierClient.payContext.onAmountChanged.remove(this.updateAmount, this);
        TastierClient.payContext.onStateChanged.remove(this.updateState, this);
        TastierClient.payContext.onIntentChanged.remove(this.intentUpdated, this);
        TastierClient.identityContext.onIdentityChange.remove(this.onAuthenticationChanged, this);
        UserCreditCardsService.onCreditCardsChanged.remove(this.onCreditCardsChanged, this);
        TastierClient.context.onCartChanged.remove(this.onCartUpdated, this);
        dataEvents.appSocket.ItemSelectionChanged.remove(this.onItemSelectionChanged, this);

        TastierClient.context.onCartDiscountChanged.remove(this.updateCurrentState, this);
        TastierClient.payContext.onBillDiscountChanged.remove(this.updateCurrentState, this);

        //Reset pay button
        this.payBtn.setAmount(toMoneyWithSign(0));

        appEvents.payContext.totalDiscountMismatchPopupViewed.remove(this.onTotalDiscountMismatchPopupViewed);
    }

    private async validateAndPay() {
        const contactDataValidationResult = await this.contactDataSection?.validateAndCreateOrUpdateContactData(true);
        const ordererRequirements = Helper.getCurrentOrderServiceOrderApplication()?.ordererRequirements;

        if (
            (ordererRequirements?.enabledFieldRequirementsCount ?? 0) > 0 &&
            contactDataValidationResult.isValid == false
        ) {
            return;
        }

        // Confirm email if required / optional and provided
        if (ordererRequirements?.isEmailRequirementEnabled === true) {
            const contactData = contactDataValidationResult.contactData;
            if (contactData!.email != null && contactData!.email.length > 0) {
                this.showIsEmailCorrectPopup(contactData!.email, async () => {
                    await this.payForSelectedItems();
                });
                return;
            }
        }

        await this.payForSelectedItems();
    }

    private setApplyDiscountSectionVisibility(forceHide: boolean = false) {
        const method = TastierClient.payContext.selectedPaymentMethod;
        const isPayService = Helper.isPayService();
        const showApplyDiscountSection = isPayService
            ? getIsApplyVouchersAllowedForPayUseCase(method, Helper.getPayUseCase())
            : getIsApplyVouchersAllowedForOrderUseCase(method, Helper.getCurrentOrderServiceOrderApplication());
        if (!forceHide && showApplyDiscountSection) {
            this.$applyDiscountSectionContainer.show();
        } else {
            this.$applyDiscountSectionContainer.hide();
        }
    }

    private async payForSelectedItems() {
        const selectedItems = TastierClient.context.getSelectedItems();

        await TastierClient.payContext.pay({
            selectedItems,
            consumption: this.getConsumption(),
            totalDiscount: this.currentState.discount.value,
            force: false,
            onClickBackBtn: () => {
                if (this.onGoBack) {
                    this.onGoBack();
                }
            },
        });
    }

    private showIsEmailCorrectPopup(email: string, onConfirm: () => void) {
        Popup.show(
            translations.pay.isEmailCorrectQuestion.replace("{0}", email),
            onConfirm,
            translations.general.yes,
            translations.general.cancel,
            "email-confirmation-popup",
            false,
            true,
            true
        );
    }

    private updateAmount() {
        // we calculate total here, because consumption should not change after entering this page!
        const tip = TastierClient.payContext.tipAmount ?? 0;
        const fees = TastierClient.payContext.fees ?? 0;
        const total = Misc.roundCents(this.getTotalAfterDiscount() + tip + fees);
        this.payBtn.setAmount(toMoneyWithSign(total, true));

        this.currentState.tip.value = tip;
        this.currentState.fees.value = fees;

        console.log("Fee in update amount " + fees);
    }

    private getConsumption() {
        return this.currentState.consumption.value;
    }

    private getTotalAfterDiscount() {
        return this.currentState.totalAfterDiscount.value;
    }

    private updateState() {
        const isPayLater = TastierClient.payContext.isPayLater();

        this.updateTitle(isPayLater);
        this.updateMethodSelectorPaymentInProgress();
        this.updateButtonSize();

        if (!this.useTip || !TastierClient.payContext.isTipAllowedForSelectedPaymentMethod()) {
            this.enableDisableTip();
        }

        this.updatePayButtonState(isPayLater);
        this.currentState.isPayService.value = Helper.isPayService();
    }

    private updateTitle(isPayLater: boolean) {
        if (isPayLater) {
            this.$title.text(trans.pay.order);
        } else {
            this.$title.text(trans.pay.title);
        }
    }

    private updateMethodSelectorPaymentInProgress() {
        const isOpen = this.paymentMethodSelectorComponent?.isOpen;
        const isPaying = TastierClient.payContext.state === UiPaymentState.Paying;
        const isOrdering = TastierClient.payContext.state === UiPaymentState.Ordering;
        const isOpenAndCardFormModeAndPaying =
            isOpen &&
            this.paymentMethodSelectorComponent?.paymentMethodSelectorMode ===
                PaymentMethodSelectorMode.StripeCardForm &&
            isPaying;
        const isClosedAndPayingOrOrdering = !isOpen && (isPaying || isOrdering);

        this.paymentMethodSelectorComponent?.setPaymentInProgress(
            isOpenAndCardFormModeAndPaying || isClosedAndPayingOrOrdering
        );
    }

    private updateButtonSize() {
        if (this.paymentMethodSelectorComponent?.isOpen) {
            this.$bottomContainer.removeClass("moveaside");
        } else {
            this.$bottomContainer.addClass("moveaside");
        }
    }

    private updatePayButtonState(isPayLater: boolean) {
        if (this.paymentMethodSelectorComponent?.isOpen) {
            if (this.paymentMethodSelectorComponent.paymentMethodSelectorMode === PaymentMethodSelectorMode.Default) {
                this.payBtn.setDisabled();
                this.setPaymentSelectorsDisabled();
            } else {
                if (this.isPayingState()) {
                    this.payBtn.setDisabled(trans.pay.paying);
                    this.setPaymentSelectorsDisabled();
                } else if (isPayLater) {
                    this.setPaymentSelectorsEnabled();
                    this.payBtn.setEnabled();
                    this.payBtn.setAction(trans.pay.order);
                } else {
                    this.setPaymentSelectorsEnabled();
                    this.payBtn.setEnabled();
                    this.payBtn.setAction(translations.pay.payNow);
                }
            }
        } else {
            switch (TastierClient.payContext.state) {
                case UiPaymentState.Preparing:
                    this.payBtn.setDisabled();
                    this.payBtn.setAction(translations.pay.payNow);
                    break;
                case UiPaymentState.MethodSelection:
                    this.setPaymentSelectorsEnabled();
                    this.payBtn.setDisabled(undefined, true);
                    this.payBtn.setAction(translations.pay.payNow);
                    break;
                case UiPaymentState.ReadyToPay:
                    this.payBtn.setEnabled();
                    this.setPaymentSelectorsEnabled();
                    if (isPayLater) {
                        this.payBtn.setAction(trans.pay.order);
                    } else {
                        this.payBtn.setAction(translations.pay.payNow);
                    }
                    break;
                case UiPaymentState.Paying:
                    this.payBtn.setDisabled(trans.pay.paying);
                    this.setPaymentSelectorsDisabled();
                    break;
                case UiPaymentState.Confirmed:
                    this.payBtn.setDisabled(trans.pay.btnSuccessful);
                    this.setPaymentSelectorsDisabled();
                    break;
                case UiPaymentState.Failed:
                    this.payBtn.setDisabled(trans.pay.btnFailed);
                    this.setPaymentSelectorsDisabled();
                    break;
                case UiPaymentState.Ordering:
                    this.payBtn.setDisabled(trans.pay.ordering);
                    this.setPaymentSelectorsDisabled();
                    break;
            }
        }
    }

    private onItemSelectionChanged(eventData: BillItemSelectionChangedEvent) {
        if (eventData.identityId !== TastierClient.identityContext.getIdentityId()) {
            return;
        }

        if (eventData.reason == UserItemSelectionChangeReason.SelectionsReset) {
            this.shouldShowSelectionsChangedPopupOnLeave = true;
        }
    }

    private setPaymentSelectorsDisabled() {
        this.quickPaymentMethodSelector.setDisabled();
    }

    private setPaymentSelectorsEnabled() {
        this.quickPaymentMethodSelector.setEnabled();
    }

    private async onPaymentMethodSelected(selectedMethod: IPaymentMethod) {
        if (selectedMethod.type === "OpenCashDrawer") {
            try {
                const roomId = TastierClient.context.currentAllocation?.roomId;
                if (roomId == null) {
                    throw new Error("Room id not specified. The command to send open cash drawer cannot be sent");
                }

                const operatorDao = Factory.getInstance<OperatorDao>(OperatorDao);
                await operatorDao.sendOpenCashDrawerToPrinterCommand({ roomId: roomId });
            } catch (e) {
                logError(e);
                Popup.show(translations.general.operationFailed);
            }
        } else {
            TastierClient.payContext.setPaymentMethod(selectedMethod, undefined);
            await this.validateAndPay();
        }
    }

    private async intentUpdated(intent: IPaymentIntent) {
        this.quickPaymentMethodSelector.setPaymentMethodButtons(intent.quickPaymentMethods);
        await this.paymentMethodSelectorComponent?.setAvailableOptions(intent.paymentMethods);
    }

    private async onAuthenticationChanged() {
        if (this.isPayingState()) {
            return;
        }

        await this.prepareForCheckout();
        this.checkLoginButtonVisibility();
    }

    private async onCreditCardsChanged() {
        if (this.isPayingState()) {
            return;
        }

        await this.prepareForCheckout();
    }

    private async prepareForCheckout() {
        this.paymentMethodSelectorComponent?.setLoading();
        try {
            await TastierClient.payContext.prepareForCheckout();
        } catch (ex) {
            logError(ex);
        }
    }

    private isPayingState() {
        return TastierClient.payContext.state === UiPaymentState.Paying;
    }

    private checkLoginButtonVisibility() {
        const isAuthenticated = TastierClient.identityContext.isAuthenticated();
        const useCase = Helper.getCurrentOrderServiceOrderApplication();

        if (isAuthenticated || (useCase !== undefined && !useCase.enableSignUpBeforePay)) {
            this.$signinSection.hide();
        } else {
            this.$signinSection.show();
        }
    }

    private updateCurrentState() {
        TastierClient.payContext.renewConsumption();
        this.currentState.totalAfterDiscount.value = TastierClient.payContext.totalExclTipAfterDiscounts;
        this.currentState.consumption.value = TastierClient.payContext.consumption;
        this.currentState.discount.value = TastierClient.payContext.discountAmount;
        this.currentState.fees.value = TastierClient.payContext.fees;
        this.currentState.isPayService.value = Helper.isPayService();

        this.updateAmount();
    }

    private onCartUpdated(isUserAction: boolean) {
        // don't notify if cart is already checked out / payment going on
        const statesToSkip = [UiPaymentState.Paying, UiPaymentState.Confirmed, UiPaymentState.Failed];

        if (statesToSkip.includes(TastierClient.payContext.state)) return;

        if (isUserAction)
            return;

        Popup.show(
            "<b>" +
                Format.htmlEscape(translations.pay.cartChangedTitle) +
                "</b><br /><br />" +
                Format.htmlEscape(translations.pay.cartChangedMessage),
            () => {
                if (this.onGoBack !== undefined) this.onGoBack();
            },
            undefined,
            undefined,
            "totalChanged"
        );
    }

    private showHideTip(forceHide: boolean = false) {
        if (!forceHide && this.useTip) {
            this.$tip.show();
        } else {
            this.$tip.hide();
        }
    }

    private enableDisableTip(forceHide: boolean = false) {
        if (forceHide) {
            this.$tip.hide();
            return;
        }

        const useCase = Helper.getCurrentUseCase();
        this.useTip =
            TastierClient.payContext.consumption >= 0 &&
            useCase?.useTip == true &&
            TastierClient.payContext.isTipAllowedForSelectedPaymentMethod();

        if (this.useTip) {
            const newTipPercent = this.$tip.is(":visible")
                ? TastierClient.payContext.tipPercent
                : TastierClient.payContext.defaultTipPercent;

            this.$tip.show();
            this.currentState.showTip.value = true;
            if (TastierClient.identityContext?.isWaiterInCurrentLocation()) {
                TastierClient.payContext.setTip(0);
                this.tipInput!.setValue();
                this.receivedInput!.setValue();
            } else {
                this.tipSlider!.setValue(newTipPercent!).resize();
                TastierClient.payContext.setTipInPercent(newTipPercent!);
            }
        } else {
            this.currentState.showTip.value = false;
            TastierClient.payContext.setTipInPercent(0);
            this.$tip.hide();
            this.tipSlider?.setValue(0);
            this.tipInput?.setValue(0);
        }
    }

    private async onTotalDiscountMismatchPopupViewed() {
        if (this.currentState.isPayService.value) {
            if (TastierClient.context.currentBill?.id) {
                const currentBill = await this.customerDao.getCurrentBill({
                    billId: TastierClient.context.currentBill?.id,
                });
                this.currentState.discount.value = currentBill?.selectionTotalDiscountsAndVouchersAmount ?? 0;
                this.currentState.totalAfterDiscount.value =
                    this.currentState.consumption.value - this.currentState.discount.value;
            }
        } else {
            try {
                const currentCart = await this.customerDao.getCart();
                this.currentState.discount.value = currentCart?.totalDiscountedAmount ?? 0;
                this.currentState.totalAfterDiscount.value =
                    this.currentState.consumption.value - this.currentState.discount.value;
            } catch (ex) {
                logError(ex);
            }
        }
        this.updateAmount();
    }
}
